import { useEffect, useState } from 'react';
import { toLocalIsoDate } from '@/utils/localDate';

/** Slack after midnight so a slightly early timer never re-reads the old day. */
const MIDNIGHT_SLACK_MS = 1_000;

/**
 * Today's local calendar day as `YYYY-MM-DD`, kept current across midnight:
 * re-read by a timer armed for the next local midnight and whenever the page
 * becomes visible again (timers are throttled / frozen in background tabs and
 * suspended WebViews).
 */
export function useLocalToday(): string {
  const [today, setToday] = useState(() => toLocalIsoDate(new Date()));

  useEffect(() => {
    const refresh = () => setToday(toLocalIsoDate(new Date()));
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timer = window.setTimeout(refresh, nextMidnight.getTime() - now.getTime() + MIDNIGHT_SLACK_MS);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // Re-armed on every day change so the timer always targets the next midnight.
  }, [today]);

  return today;
}
